'use client';

import {
  AnchorHTMLAttributes,
  createContext,
  DetailedHTMLProps,
  HTMLProps,
  MouseEvent,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react';

import dynamic from 'next/dynamic';
import Image, { StaticImageData } from 'next/image';
import Link, { LinkProps } from 'next/link';

import { useAuthContext } from '@/components/Providers/AuthProvider';
import Dropdown from '@/components/V2/DropdownMenu';
import { NEXT_MEDIA_PATH } from '@/config/env';
import cn from '@/helpers/utils/classNames';
import { useAppContext } from '@/layouts/AppProvider';
import { useAppSelector } from '@/store';
import { CustomLink } from '@/types/Common';

const PetproInformationModal = dynamic(() => import('./PetproInformationModal'));

const BrandingBarContext = createContext<string | null>(null);
function useBrandingBarContext() {
  const context = useContext(BrandingBarContext);
  if (!context) throw new Error('BrandingBar.* can only be used as direct childs of BrandingBar');

  return context;
}
type UserSettingsItem = {
  name: string;
  link: string;
  onClick?: (e?: MouseEvent) => void;
};
export interface CurrentUserSectionProps
  extends Omit<DetailedHTMLProps<AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>, 'className'> {
  className?: string;

  userSettings: CustomLink[];
}

type RawBrandingProps = {
  brandImage: StaticImageData;
  className?: string;
  logoClassName?: string;
  href: string;
};

export type BrandingProps = RawBrandingProps &
  (
    | ({
        as?: 'Link';
      } & Omit<LinkProps, 'href' | 'ref'>)
    | ({
        as?: 'a';
      } & Omit<HTMLProps<HTMLAnchorElement>, 'href' | 'ref'>)
  );

function CurrentUserSection(props: CurrentUserSectionProps) {
  useBrandingBarContext();
  const { className, userSettings, ...restProps } = props;
  const [isOpen, setIsOpen] = useState(false);
  const { isBackOffice } = useAppContext();
  const { customer, customerAgainstWebAlias } = useAuthContext();
  const isAuthenticated = useAppSelector(store => store.slices.authenticationSlice.isAuthenticated);

  const handleOpenModal = (event: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>) => {
    event.preventDefault();

    if (!isBackOffice && customerAgainstWebAlias?.customerID) setIsOpen(true);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
  };
  const name = `${customerAgainstWebAlias?.firstName ?? ''} ${customerAgainstWebAlias?.lastName ?? ''}`.trim();

  const backOfficeUserName = customer?.customerID ? `Welcome ${customer?.company || ''} (${customer?.customerID})` : '';
  const image = isBackOffice
    ? customer?.avatarUrl
      ? `${NEXT_MEDIA_PATH}${customer.avatarUrl ?? ''}`
      : ''
    : customerAgainstWebAlias?.avatarUrl
    ? `${NEXT_MEDIA_PATH}${customerAgainstWebAlias.avatarUrl ?? ''}`
    : '';

  return (
    <div className={cn('v2-flex v2-items-center v2-font-normal', className)}>
      <span {...restProps} onClick={handleOpenModal} className={cn('v2-flex v2-items-center v2-cursor-pointer')}>
        <div className="v2-relative">
          {image && (
            <Image
              alt=""
              src={image}
              width={84}
              height={84}
              loading="lazy"
              className="v2-inline v2-rounded-full v2-w-[100px] v2-h-[100px] md:v2-w-[100px] md:v2-h-[100px] sm:v2-h-[48px] sm:v2-w-[48px] xs:v2-h-[48px] xs:v2-w-[48px] v2-border-8 md:v2-border-8 sm:v2-border-[1px] xs:v2-border-[1px] v2-border-[#f7f6f2] bottom-[#f8f6f2]"
            />
          )}
          {isAuthenticated && (
            <Dropdown
              label={backOfficeUserName}
              labelClassName="sm:v2-hidden xs:v2-hidden xxs:v2-hidden md:v2-block v2-font-medium sm:v2-hidden v2-block"
              baseWrapperClassName="v2-whitespace-nowrap !v2-px-0 v2-z-[2] v2-absolute v2-bottom-[1px] v2-right-[1px] sm:v2-hidden v2-block"
              arrowClassName="v2-bg-white v2-rounded-full v2-border !v2-h-4 !v2-w-4"
            >
              <Dropdown.SingleColumnLayout className="!v2-mt-2">
                <>
                  <li>
                    <div className="v2-mb-2">
                      <h5 className="v2-text-base">{customer?.fullName}</h5>
                      <small>ID# {customer?.customerID}</small>
                    </div>
                  </li>
                  {userSettings.map(({ name, link, onClick }) => (
                    <Dropdown.SingleColumnLayout.ColumnItem
                      as={'a'}
                      key={name + link}
                      href={link}
                      label={name}
                      onClick={onClick}
                      labelClassName="!v2-text-black !v2-font-semibold !v2-text-xs !v2-font-sweet-sans-pro-semi-bold"
                      className="v2-pb-2"
                    />
                  ))}
                </>
              </Dropdown.SingleColumnLayout>
            </Dropdown>
          )}
        </div>

        <span className="sm:v2-inline v2-pl-5 v2-text-gray-900 v2-font-sweet-sans-pro v2-font-medium v2-text-base v2-hidden">
          {isBackOffice ? backOfficeUserName : name}
        </span>
      </span>

      {isOpen && <PetproInformationModal isOpen={isOpen} onClose={handleCloseModal} />}
    </div>
  );
}

function BrandingLink(props: BrandingProps) {
  useBrandingBarContext();
  const { brandImage, className, logoClassName, as, ...branding } = props;

  const Comp = as === 'a' ? 'a' : Link;
  return (
    <div className={cn('v2-text-center', className)}>
      <Comp {...branding}>
        <Image className={cn(logoClassName, 'v2-mx-auto')} loading="lazy" src={brandImage} width={200} alt="pawTree" />
      </Comp>
    </div>
  );
}

interface RawNLinkProps {
  children: ReactNode;
  className?: string;
  wrapperClassName?: string;
  href: string;
}

type NLinkProps = RawNLinkProps &
  (
    | ({
        as?: 'Link';
      } & Omit<LinkProps, 'href' | 'ref'>)
    | ({
        as?: 'a';
      } & Omit<HTMLProps<HTMLAnchorElement>, 'href' | 'ref'>)
  );

interface RawPointsBadgeProps {
  badgeText: string;
  value: number | string;
  className?: string;
  badgeClassName?: string;
  href: string;
}

type PointsBadgeProps = RawPointsBadgeProps &
  (
    | ({
        as?: 'Link';
      } & Omit<LinkProps, 'href' | 'ref'>)
    | ({
        as?: 'a';
      } & Omit<HTMLProps<HTMLAnchorElement>, 'href' | 'ref'>)
  );

function NLink(props: NLinkProps) {
  useBrandingBarContext();
  const { className, wrapperClassName, as, children, ...restProps } = props;
  const Comp = as === 'a' ? 'a' : Link;

  return (
    <div className={cn('v2-whitespace-nowrap v2-leading-4', wrapperClassName)}>
      <Comp className={cn(`v2-flex v2-items-center`, className)} {...restProps}>
        {children}
      </Comp>
    </div>
  );
}

function PointsBadge({ badgeText, value, className, badgeClassName, as, href, ...restLinkProps }: PointsBadgeProps) {
  useBrandingBarContext();

  const displayText = useMemo(() => {
    const textList = badgeText.split(' ');
    return textList.reduce((acc, curr) => acc + curr[0]?.toUpperCase() || '', '');
  }, [badgeText]);

  const Comp = as === 'a' ? 'a' : Link;

  return (
    <div className={cn('v2-flex v2-items-center', className)} title={badgeText}>
      <div
        className={cn(
          'v2-flex v2-items-center v2-justify-center v2-rounded-full v2-bg-[#48a23f] v2-p-2 v2-text-white sm:v2-h-8 sm:v2-w-8 v2-w-6 v2-h-6',
          badgeClassName
        )}
      >
        <Comp href={href} className="v2-leading-none v2-text-xs" {...restLinkProps}>
          {displayText}
        </Comp>
      </div>
      <span className="v2-font-sweet-sans-pro v2-font-normal v2-text-gray-900 v2-text-base md:v2-text-base sm:v2-text-sm xs:v2-text-sm xs:v2-max-w-[39px] xs:v2-text-ellipsis xs:v2-overflow-hidden">
        {value}
      </span>
    </div>
  );
}

function NavigationSection(props: { children: ReactNode; className?: string }) {
  useBrandingBarContext();
  const { children, className } = props;
  return (
    <div
      className={cn(
        'v2-flex v2-items-center v2-justify-end v2-gap-[26px] md:v2-gap-[26px] sm:v2-gap-[4px] xs:v2-gap-[4px] v2-text-base v2-font-light xxs:v2-float-right xxs:v2-pt-0',
        className
      )}
    >
      {children}
    </div>
  );
}

export default function BrandingBar(props: { children: ReactNode; className?: string }) {
  const { children, className } = props;
  // gap-auto mt-4 mb-2 grid items-center py-2 px-10 sm:grid-cols-2 sm:px-2 md:mx-3 md:grid-cols-3 lg:mx-10 xs:gap-0
  return (
    <BrandingBarContext.Provider value={'brandingbar'}>
      {/* <div className={cn('v2-flex v2-items-center v2-justify-between', className)}>{children}</div> */}
      <div className={cn('v2-grid v2-grid-cols-3 v2-items-center', className)}>{children}</div>
    </BrandingBarContext.Provider>
  );
}

NavigationSection.NLink = NLink;
NavigationSection.PointsBadge = PointsBadge;

BrandingBar.CurrentUserSection = CurrentUserSection;
BrandingBar.BrandingLink = BrandingLink;
BrandingBar.NavigationSection = NavigationSection;
